import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  MessageCircle,
  Search,
  Filter,
  MoreVertical,
  Send,
  Phone,
  Video,
  User,
  Clock,
  Check,
  CheckCheck
} from "lucide-react";

const LiveChats = () => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Mock chat data
  useEffect(() => {
    setChats([
      {
        id: 1,
        name: "John Doe",
        phone: "+1234567890",
        lastMessage: "Hello, I need help with my order",
        timestamp: "2 min ago",
        unread: 2,
        status: "online",
        avatar: null
      },
      {
        id: 2,
        name: "Jane Smith",
        phone: "+0987654321",
        lastMessage: "Thank you for your assistance!",
        timestamp: "1 hour ago",
        unread: 0,
        status: "offline",
        avatar: null
      },
      {
        id: 3,
        name: "Mike Johnson",
        phone: "+1122334455",
        lastMessage: "When will my package arrive?",
        timestamp: "3 hours ago",
        unread: 1,
        status: "online",
        avatar: null
      }
    ]);
  }, []);

  const filteredChats = chats.filter(chat =>
    chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.phone.includes(searchQuery)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-3 sm:px-4 lg:px-5 py-4">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">
            Live Chats
          </h1>
          <p className="text-xs text-gray-600">
            Manage real-time conversations with your customers
          </p>
        </div>

        {/* Main Chat Container */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex h-[calc(100vh-12rem)]">
            {/* Chat List Sidebar */}
            <div className="w-full md:w-80 border-r border-gray-200 flex flex-col">
              {/* Search Bar */}
              <div className="p-3 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/40 focus:border-primary transition bg-white"
                  />
                </div>
              </div>

              {/* Chat List */}
              <div className="flex-1 overflow-y-auto scrollbar-hide">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                  </div>
                ) : filteredChats.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No conversations found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredChats.map((chat) => (
                      <motion.div
                        key={chat.id}
                        onClick={() => setSelectedChat(chat)}
                        className={`
                          p-3 cursor-pointer transition-colors duration-200
                          ${selectedChat?.id === chat.id ? "bg-primary/5" : "hover:bg-gray-50"}
                        `}
                        whileHover={{ backgroundColor: "rgba(0,0,0,0.02)" }}
                      >
                        <div className="flex items-start gap-2.5">
                          {/* Avatar */}
                          <div className="relative flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <User size={18} className="text-primary" />
                            </div>
                            {chat.status === "online" && (
                              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                            )}
                          </div>

                          {/* Chat Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="text-sm font-semibold text-gray-900 truncate">
                                {chat.name}
                              </h3>
                              <span className="text-[10px] text-gray-500 flex-shrink-0 ml-2">
                                {chat.timestamp}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-600 truncate">
                                {chat.lastMessage}
                              </p>
                              {chat.unread > 0 && (
                                <span className="flex-shrink-0 ml-2 bg-primary text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                                  {chat.unread}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Chat Window */}
            <div className="hidden md:flex flex-1 flex-col">
              {selectedChat ? (
                <>
                  {/* Chat Header */}
                  <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User size={18} className="text-primary" />
                        </div>
                        {selectedChat.status === "online" && (
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                        )}
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          {selectedChat.name}
                        </h3>
                        <p className="text-xs text-gray-500">{selectedChat.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button className="p-2 rounded-lg hover:bg-gray-200 transition-colors">
                        <Phone size={16} className="text-gray-600" />
                      </button>
                      <button className="p-2 rounded-lg hover:bg-gray-200 transition-colors">
                        <Video size={16} className="text-gray-600" />
                      </button>
                      <button className="p-2 rounded-lg hover:bg-gray-200 transition-colors">
                        <MoreVertical size={16} className="text-gray-600" />
                      </button>
                    </div>
                  </div>

                  {/* Messages Area */}
                  <div className="flex-1 overflow-y-auto p-4 bg-gray-50 scrollbar-hide">
                    <div className="space-y-3">
                      {/* Sample Messages */}
                      <div className="flex justify-start">
                        <div className="max-w-[70%] bg-white rounded-lg px-3 py-2 shadow-sm">
                          <p className="text-sm text-gray-900">Hello, I need help with my order</p>
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-[10px] text-gray-500">2 min ago</span>
                            <CheckCheck size={12} className="text-blue-500" />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <div className="max-w-[70%] bg-primary text-white rounded-lg px-3 py-2">
                          <p className="text-sm">Hi! I'd be happy to help you with your order. What's your order number?</p>
                          <div className="flex items-center gap-1 mt-1 justify-end">
                            <span className="text-[10px] text-white/80">1 min ago</span>
                            <CheckCheck size={12} className="text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Message Input */}
                  <div className="p-3 border-t border-gray-200 bg-white">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Type a message..."
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none"
                      />
                      <button className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Select a conversation to start chatting</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveChats;

